import { ReactNode, SyntheticEvent } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Tab, Tabs as MuiTabs } from '@mui/material';
import { usePostHog } from 'posthog-js/react';

import { usePageInformationContext } from '@/context/PageInformationContext';

interface Tab {
  name: string;
  disabled?: boolean;
  label?: ReactNode;
  closable?: boolean;
  onClose?: (value: number) => void;
  tag?: ReactNode;
}

interface CustomTabsProps {
  tabs: Tab[];
  activeTab: number;
  onChange?: (index: number) => void;
  height?: number;
  minTabWidth?: number;
  offset?: number;
  maxTabWidth?: number | 'unset';
  scrollButtons?: boolean | 'auto';
}

const CloseButton = ({
  label,
  name,
  onClose,
  index
}: {
  label: ReactNode | undefined;
  index: number;
  name: string;
  onClose?: (value: number) => void;
}) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {label || name}

      <Box
        role='button'
        sx={{
          ml: 1,
          width: 32,
          height: 32,
          padding: 0.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
        onClick={e => {
          e.stopPropagation();
          if (onClose) onClose(index);
        }}
      >
        <CloseIcon sx={{ fontSize: 16 }} /> {/* Smaller icon */}
      </Box>
    </Box>
  );
};

const CustomTabs = ({
  tabs,
  activeTab,
  onChange,
  height = 48,
  minTabWidth = 180,
  maxTabWidth = 'unset',
  offset = 0,
  scrollButtons = false,
  ...tabProps
}: CustomTabsProps) => {
  const handleChange = (event: SyntheticEvent, newValue: number) => {
    if (onChange) onChange(newValue);
  };

  const { mainPageTitle, subPageTitle } = usePageInformationContext();

  const postHog = usePostHog();
  const handleTabClick = (
    mainPage: string | undefined,
    subPage: string | undefined,
    tabName: string
  ) => {
    postHog?.capture('tab_click', {
      mainPage,
      subPage,
      tabName
    });
  };
  return (
    <MuiTabs
      aria-label='tabs'
      scrollButtons={scrollButtons}
      sx={{
        maxWidth: `calc(100% - ${offset}px)`,
        minHeight: `${height}px`,
        height,
        '& .MuiTabs-scrollButtons.Mui-disabled': {
          display: 'none'
        }
      }}
      value={activeTab}
      variant='scrollable'
      onChange={handleChange}
    >
      {tabs?.map(({ name, label, disabled, closable, onClose, tag }, index) => (
        <Tab
          key={`tab-${name}-${index}`}
          aria-controls={`tabpanel-${name}`}
          disabled={disabled}
          id={`tab-${name}`}
          label={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                width: '100%',
                height: '100%',
                textAlign: 'center'
              }}
            >
              {closable ? (
                <CloseButton
                  index={index}
                  label={label}
                  name={name}
                  onClose={onClose}
                />
              ) : (
                <Box
                  sx={{
                    flexBasis: '100%'
                  }}
                >
                  {label || name}
                </Box>
              )}
              {tag}
            </Box>
          }
          sx={{
            alignItems: 'flex-start',
            textTransform: 'initial',
            height,
            minWidth: `${minTabWidth}px`,
            maxWidth: `${maxTabWidth}px`,
            minHeight: `${height}px`,
            p: 1,
            '&:not(:last-child)': { mr: '2px' }
          }}
          onClick={() => {
            handleTabClick(mainPageTitle, subPageTitle, name);
          }}
          {...tabProps}
        />
      ))}
    </MuiTabs>
  );
};

export default CustomTabs;
